import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { getDataWithComp, getAlert } from "../../../../utils/SamuiUtils";
import { SUMMARY_FIELDS, DETAIL_FIELDS } from "./Fields";

export default function useTopSellingProducts({
  startDate,
  endDate,
  search,
  sortField,
  sortOrder,
  page,
  perPage,
  statusFilter,
}) {
  const [rawData, setRawData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [detailInfo, setDetailInfo] = useState({ index: 0, code: "", name: "" });

  useEffect(() => {
    getDataWithComp("API_0702_SO_D", "Order By Item_Code DESC")
      .then(setRawData)
      .catch((e) => getAlert("FAILED", e.message));
  }, []);

  const byDate = useMemo(
    () => rawData.filter((i) => {
      const d = moment(i.Doc_Date).format("YYYY-MM-DD");
      return d >= startDate && d <= endDate;
    }),
    [rawData, startDate, endDate]
  );

  const grouped = useMemo(() => {
    const m = {};
    byDate.forEach((item) => {
      const key = item.Item_Code;
      if (!key) return;
      if (!item.Item_Name) item.Item_Name = key;
      const ar = item.AR_Code;
      if (!m[key]) {
        m[key] = {
          ...item,
          purchaseCount: 1,
          ...SUMMARY_FIELDS.reduce((acc, f) => {
            if (f.numeric) acc[f.key] = +item[f.key] || 0;
            return acc;
          }, {}),
          TotalQty: +item.Item_Qty || 0,
          Item_Total: +item.Item_Total || 0,
          _customerCounts: { [ar]: 1 },
          _customersSet: new Set([ar]),
        };
      } else {
        SUMMARY_FIELDS.forEach((f) => {
          if (f.numeric) m[key][f.key] += +item[f.key] || 0;
        });
        m[key].TotalQty += +item.Item_Qty || 0;
        m[key].Item_Total += +item.Item_Total || 0;
        m[key].purchaseCount += 1;
        m[key]._customerCounts[ar] = (m[key]._customerCounts[ar] || 0) + 1;
        m[key]._customersSet.add(ar);
      }
    });
    return Object.values(m).map((entry) => {
      const total_customer = entry._customersSet.size;
      const total_customer_repeat = Object.values(entry._customerCounts).filter(
        (c) => c > 1
      ).length;
      const repeat_rate = total_customer
        ? parseFloat(((total_customer_repeat / total_customer) * 100).toFixed(2))
        : 0;
      const { _customerCounts, _customersSet, ...rest } = entry;
      return { ...rest, total_customer, total_customer_repeat, repeat_rate, purchaseCount: entry.purchaseCount };
    });
  }, [byDate]);

  const filtered = useMemo(() => {
    let a = statusFilter
      ? grouped.filter(i => i.DocStatusPaid_Name === statusFilter)
      : grouped;
    if (search) {
      const q = search.toLowerCase();
      a = a.filter(i => Object.values(i).some(v => v?.toString().toLowerCase().includes(q)));
    }
    return a;
  }, [grouped, statusFilter, search]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[sortField] ?? "";
      const vb = b[sortField] ?? "";
      if (!isNaN(+va) && !isNaN(+vb)) return sortOrder === "asc" ? va - vb : vb - va;
      return sortOrder === "asc"
        ? va.toString().localeCompare(vb)
        : vb.toString().localeCompare(va);
    });
  }, [filtered, sortField, sortOrder]);

  const total = sorted.length;
  const pages = perPage > 0 ? Math.ceil(total / perPage) : 1;
  const current = useMemo(
    () => perPage > 0 ? sorted.slice((page - 1) * perPage, page * perPage) : sorted,
    [sorted, page, perPage]
  );

  const sums = useMemo(() => ({
    sumItem_Total: filtered.reduce((s, i) => s + (+i.Item_Total || 0), 0),
    sumPurchaseCount: filtered.reduce((s, i) => s + (i.purchaseCount || 0), 0),
    sumTotalCustomerRepeat: filtered.reduce((s, i) => s + (i.total_customer_repeat || 0), 0),
  }), [filtered]);

  const showDetailByIndex = useCallback(
    (idx) => {
      const prod = current[idx];
      if (!prod) return;
      const filter = [
        `AND Item_Code LIKE '%${prod.Item_Code}%'`,
        `AND Doc_Date >= '${startDate}'`,
        `AND Doc_Date <= '${endDate}'`,
        "ORDER BY Doc_Date",
      ].join(" ");
      getDataWithComp("API_0702_SO_D", filter)
        .then((list) => {
          setDetailData(list || []);
          setDetailInfo({ index: idx, code: prod.Item_Code, name: prod.Item_Name });
        })
        .catch((e) => getAlert("FAILED", e.message));
    }, [current, startDate, endDate]
  );

  return { current, total, pages, sums, detailData, detailInfo, showDetailByIndex };
}
